/**
 * ChargenController — Lit ReactiveController owning the chargen
 * (character-creation) lifecycle: the player's in-progress answers,
 * the chosen creation path, transient pack-download feedback, the
 * dynamic-import handles for the chargen + DM-review regions, and
 * the end-to-end AI synthesis pipeline.
 *
 * Extracted from `src/quire-app.ts` in Phase 3a Cluster E step 1
 * per the convergent finding from the four-reviewer Phase 2 gate:
 * the chargen flow's host-side surface had grown 4-6 @state fields,
 * 4+ methods, and 2 dynamic-import caches all woven into QuireApp,
 * and the unified DM review surface (P3T-17 ≡ CC-24 ≡ P3U-12 ≡
 * P3E-1) wanted a single seam to consume.  The host contract is
 * deliberately small: anything that's "AI keys", "session", or
 * "campaign loader" stays on QuireApp and is read through the
 * host's getter callbacks.
 *
 * The controller does NOT call out to provider HTTP itself; that
 * still lives in `src/ai/backstory-synthesizer.ts` (the frozen-
 * contract module).  The controller orchestrates: read persisted
 * answers, build player-facing context, dispatch to the synthesizer,
 * cache the result, expose it to the DM-review region.
 *
 * Storage layout: same `quire.chargen.<slug>:slot<N>` localStorage
 * keys used pre-extraction — see `src/chargen-persistence.ts`.
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { LoadedCampaign as LoadedCampaignBase } from '../campaign-loader';
import type { LoadedCharacter } from '../character-loader';

/**
 * The host wraps the loader's `LoadedCampaign` to attach a few
 * extra resolved fields (e.g. `worldOverview`).  The controller
 * only reaches into `.base` so we declare a minimal shape here
 * rather than create a circular import on the wrapper type.
 */
export interface ChargenCampaign {
  base: LoadedCampaignBase;
}
import {
  campaignFingerprint,
  encodeInviteToken
} from '../invite-token';
import { routeToSearch } from '../routing';
import {
  loadChargenState,
  saveChargenState,
  saveChargenSynthResult,
  loadChargenSynthResult,
  clearChargenSynthResult
} from '../chargen-persistence';
import { ChargenPersistenceQueue } from './chargen-persistence-queue';
import { ChargenAcceptanceMachine } from './chargen-acceptance-machine';
import { ChargenSynthLifecycle } from './chargen-synth-lifecycle';
import {
  packChargen,
  parseChargenPack,
  stringifyChargenPack,
  suggestedPackFilename,
  ChargenPackError,
  type ChargenPackDocument,
  type BondDraft,
  MAX_BOND_DRAFTS,
  CHARGEN_PACK_MAX_SIZE_BYTES
} from '../chargen-pack';
import {
  buildPlayerFacingContext,
  type ContextFile
} from '../ai/campaign-context';
import type { AiProvider as AiProviderImpl } from '../ai/broker';
import type {
  AnsweredQuestion
} from '../ai/backstory-synthesis-prompt';
import type {
  CreationPath,
  CharCreationAnswers
} from '../ui/regions/character-creation';
import type {
  SynthesizeBackstoryResult,
  ResyncContext
} from '../ai/backstory-synthesizer';
import type { PcBackstorySynthesisResponse } from '../ai/schema';

// Provider id matches the AiKeyStore vocabulary.
type AiProvider = 'claude' | 'gemini';

/**
 * Host contract — the slice of QuireApp the controller needs
 * read-only access to.  Each capability is a getter callback so the
 * controller always observes the latest state at decision time
 * (avoids stale-snapshot bugs when the host re-renders mid-flight).
 */
export interface ChargenHost {
  /** Resolved campaign, or undefined when nothing is loaded. */
  getCurrentCampaign(): ChargenCampaign | undefined;
  /** Stable campaign slug used as the chargen-persistence key prefix. */
  getCampaignSlug(campaign: ChargenCampaign): string;
  /** Currently-selected AI provider id. */
  getAiProvider(): AiProvider;
  /** API key for the active provider; empty string if unset. */
  getAiApiKey(): string;
  /** Provider model id (e.g., 'claude-sonnet-4-6'). */
  getAiModel(): string;
  /**
   * Map of provider id → provider impl for the synthesizer call.
   * Returning the whole record (rather than pre-resolving) lets the
   * controller keep its dependency surface narrow.
   */
  getAiProviders(): Record<AiProvider, AiProviderImpl>;
  /**
   * Display name of the inviting peer (the DM).  Forwarded to the
   * synthesizer so the AI knows not to reuse it as the PC's name.
   */
  getDmDisplayName(): string;
  /** Coord-gate: synthesize-and-accept paths require coordinator. */
  isCoordinator(): boolean;
  /**
   * Bound-character cache resolver for the P3U-12 display-name
   * lookup.  Returns the loaded character record (with manifest.name)
   * or null when not yet resolved; the controller treats null as
   * "fall back to pcId text".
   */
  getBoundCharacter(pcId: string): LoadedCharacter | null;
  /**
   * Trigger an async fetch of the named character file so a
   * subsequent `getBoundCharacter` resolves.  Idempotent at the
   * host (the host caches in-flight imports).
   */
  loadCharacterByPcId(pcId: string): void;
  /**
   * Append a `scratch-note` event to the audit log.  CC-24 + P3T-19:
   * accept and revise emit scratch-notes for v1 (per Cluster E
   * planning Q2 — defer a dedicated `chargen-accept` event kind to
   * Phase 3b when CC-4 per-PC SaveDocument is in scope).
   * Returns true when the append succeeded; the controller uses
   * this only for the local-flag update, not for control flow.
   */
  appendScratchNote(text: string): boolean;
  /**
   * Phase 3b-1: append a `pc-create` event that materializes a
   * synthesized PC into shared session state.  Called by
   * `acceptSlot` together with `bindPcSlot` (atomic from the
   * DM's POV) so the loop "DM clicks Accept → player has a sheet"
   * closes in one click.
   */
  appendPcCreate(payload: {
    pcId: string;
    name: string;
    pronouns: string;
    tags: string[];
    stats: {
      str: number;
      dex: number;
      con: number;
      int: number;
      wis: number;
      cha: number;
    };
    skills: string[];
    backstory: string;
    causedByResponseId?: string;
    /**
     * P-R12 (2026-05-25): catch-up advancement for mid-campaign PCs.
     * Both default to 0; the engine clamps the upper bound at 20.
     */
    startingAdvancements?: number;
    startingMarks?: number;
    /**
     * Phase B P2 (2026-05-26): AI-synthesized languages list +
     * money-band tier.  Both optional; the materializer fills
     * defaults (`['English']` / `'tight'`) when omitted.  Validated
     * (8-entry × 40-char cap on languages; enum check on moneyBand)
     * at the materializer boundary.
     */
    languages?: string[];
    moneyBand?: 'broke' | 'tight' | 'comfortable' | 'well-off' | 'wealthy';
    /**
     * Player's verbatim `intent-moment` chargen answer, preserved as
     * a first-class field on the PC.  Optional so DM-direct authoring
     * (no chargen answers pack) still works.  Bounded at the
     * materializer (0 < length ≤ 800 chars).
     */
    intentionUnderPressure?: string;
    /**
     * 2026-08-17 chargen-artifact-loss sweep: six additional chargen
     * answers preserved verbatim.  MC-value fields carry the canonical
     * option `value` (e.g. `alignment: 'lawful-evil'`); short-answer
     * fields carry the player's text.  All optional; materializer
     * validates.
     */
    alignment?:
      | 'lawful-good'
      | 'neutral-good'
      | 'chaotic-good'
      | 'lawful-neutral'
      | 'true-neutral'
      | 'chaotic-neutral'
      | 'lawful-evil'
      | 'neutral-evil'
      | 'chaotic-evil';
    meaningfulItem?: string;
    specificPlace?: string;
    temperamentUnderPressure?: string;
    priorConnectionKind?: string;
    flightReason?: string;
    intentionHorizon?: string;
  }): boolean;
  /**
   * Phase 3b-1: bind a slot to a pcId via the existing
   * `pc-slot-bind` event.  Atomically paired with appendPcCreate
   * on accept so the player sees their PC immediately.
   */
  bindPcSlot(slot: number, pcId: string): boolean;
  /**
   * D5.5-B (2026-05-27): emit a placeholder `bond-propose` for a
   * chargen-authored bond draft.  Called by acceptSlot after
   * pc-create + pc-slot-bind, once per bond the player wrote.
   * The target is a free-text placeholder (the target PC may not
   * exist yet); the DM resolves it to a real pcId at ratify.
   * Returns true on emit; false when the host gated or the
   * payload was rejected.  Each call is independent — a rejected
   * bond does NOT roll back the PC or the other bonds (proposals
   * are DM-private; a missing bond is benign).
   */
  appendBondPropose(payload: {
    pcId: string;
    targetPlaceholder: string;
    text: string;
  }): boolean;
  /**
   * Phase B-prime (2026-05-25): emit a `seat-add` event allocating
   * a new unbound seat at `slot`.  Returns true on success (and
   * the event lands in shared state), false when the seat is
   * already taken or the session isn't active.
   */
  appendSeatAdd(slot: number): boolean;
  /**
   * Wave 1 (2026-05-25): emit a `seat-remove` event for an unbound,
   * empty seat that was added accidentally.  Engine refuses to
   * touch bound seats.  Returns true on append.
   */
  appendSeatRemove(slot: number): boolean;
  /**
   * Phase B-prime (2026-05-25): read the current slot map (post
   * filter-for-viewer) so the controller can compute lowest-unused.
   * Returns an empty map when no session is active.
   */
  getPcSlots(): Record<number, { state: string; pcId?: string }>;
  /**
   * P-R2 (2026-05-25): effective seat cap from the loaded campaign
   * manifest (or DEFAULT_SEAT_CAP fallback).  The controller uses
   * it as the upper bound on addSeat's lowest-unused search.
   */
  getSeatCap(): number;
  /**
   * #253 (2026-05-26): live-WebRTC pack delivery.  The controller
   * builds the ChargenPackDocument from current local state and
   * hands it to the host, which dispatches a `chargen-pack-deliver`
   * event over the active session.  Returns false when no session
   * is active OR the pack exceeds the materializer size cap.
   */
  appendChargenPackDeliver?(
    slot: number,
    pack: ChargenPackDocument
  ): boolean;
}

/**
 * Debounce window for the chargen autosave.  Shorter than the
 * play-time autosave (300 ms vs 1500 ms) because the chargen flow
 * has fewer state changes per unit time and the player may close
 * the tab without realizing the autosave is debounced; saving
 * aggressively gives a stronger "your work is safe" guarantee.
 */
const CHARGEN_PERSIST_DEBOUNCE_MS = 300;
const PACK_FEEDBACK_CLEAR_MS = 3000;
/**
 * D5.5 first-session polish: how long the "✓ Saved" autosave
 * confirmation lingers before fading back to idle.  Long enough to
 * register ("my answers persisted") without nagging the player on
 * every keystroke.
 */
const CHARGEN_SAVED_CLEAR_MS = 2000;

export class ChargenController implements ReactiveController {
  /** Player's path selection from chargen step 3.  '' means "not chosen yet". */
  chosenPath: CreationPath | '' = '';

  /** Player's answers keyed by question id. */
  answers: CharCreationAnswers = {};

  /**
   * D5.5-B: player's chargen bond drafts (the "Connections" step).
   * 0-3 entries; persisted alongside answers + folded into the
   * pack so the DM's acceptSlot emits placeholder bond-proposes.
   */
  bondDrafts: BondDraft[] = [];

  /** Transient feedback on the "Pack my character" download. */
  packFeedback: '' | 'packed' | 'pack-failed' = '';
  /**
   * #254 (2026-05-26): AI complementarity hints per slot.  Maps
   * slot → in-flight / result so the quick-gen form can show
   * "Suggest complements" → spinner → chips → click to fill hook.
   * Per-slot because the DM might invoke quick-gen for multiple
   * seats in sequence.
   */
  complementarityHints: Map<
    number,
    {
      status: 'loading' | 'ok' | 'failed';
      suggestions: { archetype: string; hook: string }[];
      reason?: string;
    }
  > = new Map();
  /**
   * #253 (2026-05-26): transient feedback on the live "Send to DM"
   * pack delivery.  States:
   *   - 'sent'           — chargen-pack-deliver event accepted
   *   - 'send-failed'    — no active session / not in chargen mode
   *   - 'send-too-large' — stringified pack exceeds the materializer cap
   * Auto-clears via the same PACK_FEEDBACK_CLEAR_MS timer as the
   * download path.
   */
  sendToDmFeedback: '' | 'sent' | 'send-failed' | 'send-too-large' = '';

  /**
   * D5.5 first-session polish: autosave indicator for the chargen
   * intake.  Per-keystroke edits debounce-persist silently; without
   * a signal the player re-types / re-clicks wondering "did it save?"
   * during the tense first-session intake.
   *
   * Lifecycle: there is deliberately NO transient 'saving' state —
   * the 300 ms debounce is short, and a Saving↔Saved strobe on every
   * keystroke burst reads as noise (UX review).  The queue writer
   * calls `reflectSave(ok)` on flush: success → 'saved' (fades to
   * 'idle' after CHARGEN_SAVED_CLEAR_MS); failure → 'save-failed',
   * which does NOT fade (the warning stays until the next success so
   * the player isn't told their work is safe when localStorage threw).
   * Single scalar is correct for the single-slot chargen route; if a
   * caller ever drives two slots concurrently, revisit (the last
   * flush wins).  Not firewall-sensitive — reflects only the player's
   * own draft (no DM-only data).
   */
  saveState: 'idle' | 'saved' | 'save-failed' = 'idle';

  /**
   * D5.5-A step 3 (2026-05-27 E-LARGE-2): async + result lifecycle
   * cluster.  Owns synthResults + synthInFlight + resyncInFlight
   * + slotGeneration.  Pre-step-3 these were 3 controller fields +
   * 1 acceptance-machine field; engineering's post-D5.5-A review
   * flagged the resyncInFlight placement as a cluster-boundary
   * smell, so step 3 consolidates the four into one owner.
   *
   * Consumers MUST go through the public accessor methods rather
   * than mutating any internal Map/Set — a direct mutation skips
   * `host.requestUpdate` + (worse) the Scenario-4 generation-
   * counter invalidation that `clearSlot` performs.
   */
  private readonly synth = new ChargenSynthLifecycle();

  /**
   * D5.5-A step 2 (2026-05-27 E-LARGE-2): accept / pre-accept /
   * re-sync slot lifecycle state, bundled into a single owner.
   * Pre-extraction this was 8 separate fields scattered across
   * ~70 lines + 3 nearly-identical inline reset blocks.  See
   * `ChargenAcceptanceMachine` for the per-slot semantics.  Async /
   * synth-result lifecycle (synthResults, synthInFlight,
   * resyncInFlight, slotGeneration) lives in
   * `ChargenSynthLifecycle` (the `synth` field above) — different
   * lifecycle, different clear events.
   */
  private readonly acceptance = new ChargenAcceptanceMachine();

  /**
   * Code-split: cached dynamic-imports for the chargen surfaces.
   * Same lazy posture as pre-extraction — a regular play session
   * never imports these.
   */
  private chargenRegionLoaded: Promise<void> | null = null;
  private synthesizerLoaded: Promise<
    typeof import('../ai/backstory-synthesizer')
  > | null = null;

  /**
   * D5.5-A (2026-05-27 E-LARGE-2 step 1): debounced-persist queue
   * extracted into `ChargenPersistenceQueue`.  Pre-extraction this
   * lived as `persistTimers` + `persistPendingValues` + 2 methods
   * inline.  No behavior change; the queue calls
   * `saveChargenState(slug, slot, ...)` via the writer closure
   * passed in the constructor.
   */
  private readonly persistQueue = new ChargenPersistenceQueue<{
    slug: string;
    slot: number;
    chosenPath: CreationPath | '';
    answers: CharCreationAnswers;
    bondDrafts: BondDraft[];
  }>(
    (_key, value) => {
      const ok = saveChargenState(value.slug, value.slot, {
        chosenPath: value.chosenPath,
        answers: value.answers,
        bondDrafts: value.bondDrafts
      });
      this.reflectSave(ok);
    },
    CHARGEN_PERSIST_DEBOUNCE_MS
  );
  private packFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private saveStateTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly env: ChargenHost
  ) {
    host.addController(this);
  }

  hostConnected(): void {
    /* no-op — state seeds on the chargen-route navigation. */
  }

  hostDisconnected(): void {
    // Flush any pending persist timers so a tab close mid-typing
    // doesn't lose the last <300 ms of edits.  Same discipline as
    // AiKeyStore.hostDisconnected.
    this.flushPending();
    if (this.packFeedbackTimer) {
      clearTimeout(this.packFeedbackTimer);
      this.packFeedbackTimer = null;
    }
    // flushPending above may have fired reflectSave (→ a fresh
    // saveStateTimer); clear it AFTER so a route-exit flush doesn't
    // leave a timer that requestUpdate()s a torn-down host.
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = null;
    }
    this.saveState = 'idle';
    // Engine M3 (defer-followup): in-flight synthesis aborts when
    // a real AbortSignal is plumbed through; until then, just clear
    // the inflight flag so an HMR reconnect doesn't see a wedged
    // spinner.
    this.synth.clearAllSynthInFlight();
  }

  // ---- setters that re-render the host ----

  setChosenPath(path: CreationPath | ''): void {
    if (this.chosenPath === path) return;
    this.chosenPath = path;
    this.host.requestUpdate();
  }

  setAnswer(id: string, value: string): void {
    if (this.answers[id] === value) return;
    this.answers = { ...this.answers, [id]: value };
    this.host.requestUpdate();
  }

  /**
   * Replace the entire answers map (used by `seedFromStorage`).
   * Use sparingly; prefer `setAnswer` for per-keystroke writes.
   */
  setAnswers(answers: CharCreationAnswers): void {
    this.answers = answers;
    this.host.requestUpdate();
  }

  /**
   * D5.5-B: replace the bond drafts (the Connections step sends the
   * whole array on each edit).  Clamps to MAX_BOND_DRAFTS as a
   * belt-and-suspenders against a buggy caller; the UI hides "add"
   * at the cap.
   */
  setBondDrafts(drafts: BondDraft[]): void {
    this.bondDrafts = drafts.slice(0, MAX_BOND_DRAFTS);
    this.host.requestUpdate();
  }

  // ---- read accessors for the DM-review region (Engine M1) ----

  /** Latest synthesis result for the slot, or undefined. */
  getSynthResult(slot: number): SynthesizeBackstoryResult | undefined {
    return this.synth.getResult(slot);
  }

  /** True when synthesis is in-flight for the slot. */
  isSynthInFlight(slot: number): boolean {
    return this.synth.isSynthInFlight(slot);
  }

  /**
   * Wave C2 (2026-05-26): does any slot have a chargen step
   * pending DM attention?  Pending = synth in-flight, OR a synth
   * result waiting that the DM hasn't yet
   * accepted (the dm-aside mount gate calls this; when false
   * AND no unbound seats exist, the chargen-dm-review surface
   * unmounts).  Pure read; no mutation.
   */
  hasPendingSynth(): boolean {
    if (this.synth.synthInFlightSize() > 0) return true;
    for (const slot of this.synth.resultKeys()) {
      if (!this.acceptance.isAccepted(slot)) return true;
    }
    return false;
  }

  /** True when the DM has accepted the slot's current synth result. */
  isAccepted(slot: number): boolean {
    return this.acceptance.isAccepted(slot);
  }

  /**
   * Phase B P2 verification fix (S2): true when an accept attempt
   * was just refused because the synth result has been replaced by
   * a re-sync mid-review.  The UI uses this to surface a banner
   * asking the DM to re-review.  Cleared on next successful accept
   * or fresh synth.
   */
  hasAcceptRaceMismatch(slot: number): boolean {
    return this.acceptance.hasRaceMismatch(slot);
  }
  clearAcceptRaceMismatch(slot: number): void {
    if (this.acceptance.clearRaceMismatch(slot)) this.host.requestUpdate();
  }

  /**
   * Iterate over slots that have a synth result OR an accept flag.
   * Used by the DM-review region to render per-seat cards.
   */
  slotsWithSynthState(): number[] {
    const slots = new Set<number>();
    for (const s of this.synth.resultKeys()) slots.add(s);
    for (const s of this.acceptance.acceptedIterator()) slots.add(s);
    return [...slots].sort((a, b) => a - b);
  }

  /**
   * P3T-16: return the saved short-answer-key answers for a slot,
   * read from localStorage on demand.  Returns null when no
   * chargen state exists yet (player hasn't reached the chargen
   * flow or the data lives on a different device).  Used by the
   * DM-review SA-vs-backstory diff to render the player's answers
   * alongside the synthesized backstory.
   *
   * Reads localStorage on every call — cheap (small JSON parse) and
   * keeps the call site simple.  Future caching can land if the
   * cost ever shows up.
   */
  loadPersistedAnswers(slot: number): CharCreationAnswers | null {
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) return null;
    if (!Number.isInteger(slot) || slot < 1 || slot > 9) return null;
    const slug = this.env.getCampaignSlug(campaign);
    const persisted = loadChargenState(slug, slot);
    return persisted?.answers ?? null;
  }

  // ---- write accessors (step 4 wires accept/revise; step 1 stubs them out) ----

  /**
   * CC-24 accept.  Phase 3b-1 closes the loop: emits a `pc-create`
   * event (materializes the synthesized PC into shared state) +
   * `pc-slot-bind` (binds the slot to the new pcId) atomically, then
   * appends the audit scratch-note that was the v1 acceptance
   * receipt.  At session 1, one DM click takes the seat from "synth
   * result ready" to "playable PC bound" — `boundCharacter` resolves
   * via the loader-overlay (step 2), the player sees the sheet, the
   * dice-Dock gets stats, Cast macros render.
   *
   * No-ops when there's no synth result yet OR the slot was already
   * accepted OR the result is a failure.
   *
   * pcId derivation: `slot-${slot}-${first 8 chars of responseId}`.
   * Stable per synth, unique per re-synth (different responseId →
   * different hash), human-debuggable, fits `PC_ID_RE`.  Orphan
   * records from prior re-syntheses accumulate harmlessly in
   * `state.synthesizedPcs` — cleanup is a deferred-followup, not a
   * blocker for civilized players.
   */
  /**
   * Phase B-prime (2026-05-25): allocate a new unbound seat for
   * chargen.  Returns the slot allocated (lowest unused 1..9) or
   * null when the soft cap (9) is reached / not coord / no
   * campaign / session isn't active.
   */
  addSeat(): number | null {
    if (!this.env.isCoordinator()) return null;
    if (!this.env.getCurrentCampaign()) return null;
    const taken = new Set<number>();
    for (const slotStr of Object.keys(this.env.getPcSlots())) {
      taken.add(Number(slotStr));
    }
    const cap = this.env.getSeatCap();
    for (let slot = 1; slot <= cap; slot++) {
      if (taken.has(slot)) continue;
      if (this.env.appendSeatAdd(slot)) return slot;
      break; // fall through; appendSeatAdd failure means no session
    }
    return null;
  }

  /**
   * Wave 1 (2026-05-25): drop an accidentally-added seat.  Returns
   * true on append.  Refuses when seat is bound (engine also
   * refuses, but failing fast here avoids the round-trip) or when
   * the controller side has any in-flight work for that slot
   * (pending synth, displayed result, accepted) — those imply
   * "the DM is using this seat," even if the engine state says
   * unbound.  Also clears local controller-side caches.
   */
  removeSeat(slot: number): boolean {
    if (!this.env.isCoordinator()) return false;
    const slots = this.env.getPcSlots();
    const seat = slots[slot];
    if (!seat || seat.state !== 'unbound') return false;
    if (this.synth.hasResult(slot)) return false;
    if (this.synth.isSynthInFlight(slot)) return false;
    if (this.acceptance.isAccepted(slot)) return false;
    if (!this.env.appendSeatRemove(slot)) return false;
    return true;
  }

  /**
   * Wave 1 (2026-05-25): undo a `removeSeat` within its UI window
   * by re-allocating the exact slot integer.  Skips the lowest-
   * unused search that `addSeat` performs.  The engine's seat-add
   * is idempotent on bound slots so a race (someone bound the
   * slot in the 4s window) is a safe no-op.
   */
  readdSeat(slot: number): boolean {
    if (!this.env.isCoordinator()) return false;
    if (!Number.isInteger(slot) || slot < 1) return false;
    return this.env.appendSeatAdd(slot);
  }

  /**
   * Wave 2 (2026-05-25): patch one or more fields on the in-memory
   * synth result before the DM accepts.  Mutates the cached result
   * in place and, on first touch of any field, snapshots the
   * original AI value so the drift banner can render before/after.
   *
   * Pre-acceptance edits are DM-local — the synth result is only
   * in this controller's cache; no event broadcast.  The eventual
   * `acceptSlot` will read the (now-edited) result and emit a
   * pc-create with the final values.  Per the chargen-authorship
   * memory, pre-launch silent edits are appropriate; post-launch
   * (post-accept) edits route through pc-edit with a different
   * visibility model in Wave 3.
   *
   * Returns false when the slot has no ok-synth-result or the slot
   * is already accepted (post-accept edits use pc-edit).
   */
  editSynthFieldPreAccept(
    slot: number,
    patch: Partial<PcBackstorySynthesisResponse>
  ): boolean {
    if (this.acceptance.isAccepted(slot)) return false;
    // Post-R5 fix (QA-BUG-3): no edits during re-sync — the AI is
    // about to overwrite the response anyway.
    if (this.synth.isResyncInFlight(slot)) return false;
    const result = this.synth.getResult(slot);
    if (!result || !result.ok) return false;
    let original = this.acceptance.getPreAcceptOriginal(slot);
    if (!original) {
      original = {};
      this.acceptance.setPreAcceptOriginal(slot, original);
    }
    const origAny = original as Record<string, unknown>;
    // Post-R5 fix (QA-BUG-2): clone the synth-result before
    // mutating it so external references stay immutable.
    // R6 QA-F1 fix: also deep-clone the nested mutable objects
    // (stats / tags / skillMastery) so external holders of those
    // sub-objects don't observe post-edit values either.  Other
    // fields (name/pronouns/backstory/raw/responseId/tokensIn/
    // tokensOut) are primitives — shallow spread is enough.
    const respClone = deepCloneSynthResponse(
      result.response
    ) as unknown as Record<string, unknown>;
    const patchAny = patch as Record<string, unknown>;
    for (const key of Object.keys(patch)) {
      if (!(key in origAny)) origAny[key] = respClone[key];
      respClone[key] = patchAny[key];
    }
    const edited: SynthesizeBackstoryResult = {
      ...result,
      response: respClone as unknown as PcBackstorySynthesisResponse
    };
    this.synth.setResult(slot, edited);
    // BUG-3 hotfix (2026-05-30): pre-accept edits update the
    // persisted blob so a reload doesn't drop the DM's tweaks.
    this.persistSynthResultIfPossible(slot, edited);
    // Any further edit invalidates the prior pronoun-patch hint —
    // the DM has moved on or the prose changed again.
    this.acceptance.clearPronounPatched(slot);
    // Post-R5 (QA-BUG-4): edit means the DM noticed the failure
    // banner and chose a different path — clear it.
    this.acceptance.clearResyncFailure(slot);
    this.host.requestUpdate();
    return true;
  }

  /**
   * BUG-3 hotfix helper: persist a synth result to localStorage.
   * Silent on no-campaign (chargen never runs without a campaign,
   * but be defensive).
   */
  private persistSynthResultIfPossible(
    slot: number,
    result: SynthesizeBackstoryResult
  ): void {
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) return;
    const slug = this.env.getCampaignSlug(campaign);
    saveChargenSynthResult(slug, slot, result);
  }

  /**
   * Wave 2 (2026-05-25): read the drift map for a slot — the fields
   * the DM has edited pre-accept, mapped to their original AI
   * values.  Returns undefined when the slot has no recorded
   * drift.  The UI uses this to render the "Name: Mei → Mai"
   * before/after on the drift banner.
   */
  getPreAcceptDrift(
    slot: number
  ): Partial<PcBackstorySynthesisResponse> | undefined {
    return this.acceptance.getPreAcceptOriginal(slot);
  }

  /**
   * Wave 2 (2026-05-25): "Leave drift" — the DM has decided to
   * accept the field divergence between their edit and the AI's
   * original output.  Clears the drift entry so the banner stops
   * surfacing.  Pass `field` to dismiss one entry; omit to dismiss
   * all drift for the slot.  The synth result itself is not
   * touched — the edit stays.
   */
  dismissPreAcceptDrift(
    slot: number,
    field?: keyof PcBackstorySynthesisResponse
  ): void {
    const original = this.acceptance.getPreAcceptOriginal(slot);
    if (!original) return;
    if (field !== undefined) {
      delete (original as Record<string, unknown>)[field];
      if (Object.keys(original).length === 0) {
        this.acceptance.deletePreAcceptOriginal(slot);
      }
    } else {
      this.acceptance.deletePreAcceptOriginal(slot);
    }
    this.host.requestUpdate();
  }

  /**
   * Wave 2 (2026-05-25): full snapshot of all slots' drift state,
   * suitable for passing into the UI as a property.  The UI uses
   * the keys to know which slots have a banner to show.
   *
   * Post-R5 fix (QA-BUG-1): returns a shallow clone (Map + per-slot
   * Partial copies) so the host's property-pass-through doesn't
   * share identity with the internal cache.  Without this, a Lit
   * keyed-render that compares Map identity would never see drift
   * updates; today renders survive only because `requestUpdate()`
   * fires unconditionally.  Defense-in-depth + makes the API
   * harder to misuse.
   */
  preAcceptDriftMap(): Map<number, Partial<PcBackstorySynthesisResponse>> {
    const out = new Map<number, Partial<PcBackstorySynthesisResponse>>();
    for (const [slot, drift] of this.acceptance.preAcceptOriginalEntries()) {
      out.set(slot, { ...drift });
    }
    return out;
  }

  /**
   * Wave 3a (2026-05-25): apply a deterministic find-replace to the
   * cached backstory based on the patchable subset of drift entries
   * (name + pronouns).  No AI call required; appropriate for
   * "Mei → Mai" and "she/her → they/them" identifier swaps.
   *
   * Tag/skill/stat edits and substantial pronoun rewrites need
   * AI re-sync (Wave 3b).  This method silently skips those fields;
   * caller checks `patchableDriftFields(slot)` to know which were
   * deterministically applied.
   *
   * Returns true when at least one field was patched; false when
   * the slot has no patchable drift.  Dismisses the drift entries
   * that were applied so the banner reflects the new state.
   */
  patchInPlace(slot: number): boolean {
    if (this.acceptance.isAccepted(slot)) return false;
    // Post-R5 fix (QA-BUG-3): no Patch during re-sync.
    if (this.synth.isResyncInFlight(slot)) return false;
    const result = this.synth.getResult(slot);
    if (!result || !result.ok) return false;
    const drift = this.acceptance.getPreAcceptOriginal(slot);
    if (!drift) return false;
    const patchable = this.patchableDriftFields(slot);
    if (patchable.length === 0) return false;
    let text = result.response.backstory;
    for (const field of patchable) {
      if (field === 'name') {
        const old = drift.name;
        const next = result.response.name;
        if (typeof old === 'string' && typeof next === 'string' && old !== next) {
          text = replaceAll(text, old, next);
        }
      } else if (field === 'pronouns') {
        const old = drift.pronouns;
        const next = result.response.pronouns;
        if (typeof old === 'string' && typeof next === 'string' && old !== next) {
          text = applyPronounSubstitutions(text, old, next);
        }
      }
    }
    // Apply the patched backstory + dismiss the drift entries that
    // were honored.  The backstory edit itself does NOT add a new
    // drift entry — it's a direct application of existing drift.
    //
    // Post-R5 fix (QA-BUG-2): clone before mutation (same reasoning
    // as editSynthFieldPreAccept).
    // Post-R5 fix (QA-BUG-5): stash the original (pre-patch)
    // backstory so a later Re-sync uses the AI's draft as the
    // voice anchor, not the deterministically-substituted prose
    // that may carry "they was" verb-agreement glitches.
    if (!this.acceptance.hasOriginalBackstoryForResync(slot)) {
      this.acceptance.setOriginalBackstoryForResync(slot, result.response.backstory);
    }
    // R6 QA-F1 fix: same deep-clone discipline as
    // editSynthFieldPreAccept so external refs to nested
    // objects (stats / tags / skillMastery) stay immutable.
    const respClone: PcBackstorySynthesisResponse = {
      ...deepCloneSynthResponse(result.response),
      backstory: text
    };
    const patched: SynthesizeBackstoryResult = {
      ...result,
      response: respClone
    };
    this.synth.setResult(slot, patched);
    // BUG-3 hotfix: keep persisted blob in sync with patched edits.
    this.persistSynthResultIfPossible(slot, patched);
    const pronounWasPatched = patchable.includes('pronouns');
    for (const field of patchable) {
      delete (drift as Record<string, unknown>)[field];
    }
    if (Object.keys(drift).length === 0) {
      this.acceptance.deletePreAcceptOriginal(slot);
    }
    // Wave 3 polish: mark the slot so the UI can hint about
    // potential verb-agreement glitches the substitution doesn't
    // catch.  Cleared on next edit/dismiss/accept/clear.
    if (pronounWasPatched) {
      this.acceptance.markPronounPatched(slot);
    }
    this.host.requestUpdate();
    return true;
  }

  /**
   * Wave 3 polish: read whether the slot's last patchInPlace touched
   * pronouns.  UI uses this to render a "Re-sync to clean up verb
   * agreement" hint.
   */
  wasPronounRecentlyPatched(slot: number): boolean {
    return this.acceptance.hasPronounPatch(slot);
  }

  /**
   * Wave 3 polish: dismiss the pronoun-patch hint (e.g., DM saw it
   * and moved on).  Also called automatically on next edit/dismiss/
   * accept/clear for the slot.
   */
  dismissPronounPatchHint(slot: number): void {
    if (this.acceptance.clearPronounPatched(slot)) {
      this.host.requestUpdate();
    }
  }

  /**
   * Wave 3 polish: full snapshot of slots with a pending pronoun-
   * patch hint, for the UI property pass.  Post-R5 fix (QA-BUG-1):
   * returns a fresh Set so the host's @property identity check
   * sees a new value on every accessor call.
   */
  pronounPatchedSlotsSet(): ReadonlySet<number> {
    return this.acceptance.pronounPatchedSnapshot();
  }

  /**
   * Wave 3a: which drift fields can be patched deterministically
   * (find-replace, no AI).  Currently: `name` and `pronouns`.
   * Other fields (tags / skillMastery / stats) require AI re-sync
   * (Wave 3b).
   */
  patchableDriftFields(
    slot: number
  ): Array<'name' | 'pronouns'> {
    const drift = this.acceptance.getPreAcceptOriginal(slot);
    if (!drift) return [];
    const out: Array<'name' | 'pronouns'> = [];
    if ('name' in drift) out.push('name');
    if ('pronouns' in drift) out.push('pronouns');
    return out;
  }

  acceptSlot(slot: number, expectedResponseId?: string): void {
    if (this.acceptance.isAccepted(slot)) return;
    // Post-R5 fix (QA-BUG-3): refuse to commit while a re-sync is
    // in flight — the result is about to be replaced.  Belt-and-
    // suspenders alongside the UI-level gate so non-UI callers
    // (tests, hotkeys) can't sneak past.
    if (this.synth.isResyncInFlight(slot)) return;
    const result = this.synth.getResult(slot);
    if (!result || !result.ok) return; // can't accept failures
    const r = result.response;
    // Phase B P2 verification fix (S2): race-safe accept.  When the
    // caller passed an expected responseId (the synth the DM saw
    // at modal-open), refuse the commit if a re-sync has since
    // replaced the result.  Without this gate, the DM clicks
    // Accept on a Mei Tanaka they reviewed, but a re-sync's Casey
    // Park lands and gets committed instead.  Optional for
    // backward compatibility — older callers (hotkey paths, tests)
    // pass nothing and get the legacy behavior.
    if (
      expectedResponseId !== undefined &&
      r.responseId !== expectedResponseId
    ) {
      this.acceptance.markRaceMismatch(slot);
      this.host.requestUpdate();
      return;
    }
    // Clear any prior race-mismatch flag for this slot.
    this.acceptance.clearRaceMismatch(slot);

    // Derive the pcId from slot + a short hash of responseId.
    const pcId = derivePcId(slot, r.responseId);
    if (!pcId) return; // defensive: bail if responseId was unusable

    // Translate the synthesizer's uppercase PcStats → lowercase
    // CharacterRecord stats.  The materializer validates lowercase
    // only; this is the single translation point.
    const statsLower = {
      str: r.stats.STR,
      dex: r.stats.DEX,
      con: r.stats.CON,
      int: r.stats.INT,
      wis: r.stats.WIS,
      cha: r.stats.CHA
    };

    // Emit the materialization event FIRST so that when the
    // pc-slot-bind lands, the synthesizedPcs map already has the
    // record — the loader-overlay resolves in one pass.  Both
    // events come from the same peer in the same materialize call,
    // so the per-peer monotonic seq guarantees ordering on replay.
    // P-R12 (2026-05-25, TTRPG-R6 fix): seed catch-up MARKS for a
    // mid-campaign join.  Default joiningSession=1 → marks=0
    // (fresh PC baseline).  joiningSession=3 → marks=2.
    //
    // R6 correction: an earlier draft seeded BOTH startingMarks=N-1
    // AND startingAdvancements=N-1, which broke rules.md's
    // 5-marks-per-advancement economy (each advancement normally
    // costs 5 marks of play).  Seeding 2 advancements at S3
    // would have been worth 10 sessions of marks — 5x too
    // generous.  TTRPG-R6 verdict: seed marks only and let the
    // PC convert them to advancements at end-of-session like
    // everyone else.  The DM can pc-edit to override if a
    // particular campaign uses a different cadence.
    const joiningSession = this.acceptance.getJoiningSession(slot);
    const catchUp =
      joiningSession > 1
        ? { startingMarks: Math.max(0, joiningSession - 1) }
        : {};
    // Phase B P2 (2026-05-26): forward the AI-synthesized languages
    // + money-band when present.  Materializer fills sane defaults
    // (`['English']` / `'tight'`) when omitted; spread-conditional
    // keeps the payload terse for the legacy-no-P2 case.
    const phaseB: {
      languages?: string[];
      moneyBand?: 'broke' | 'tight' | 'comfortable' | 'well-off' | 'wealthy';
    } = {};
    if (r.languages !== undefined) phaseB.languages = [...r.languages];
    if (r.moneyBand !== undefined) phaseB.moneyBand = r.moneyBand;

    // Preserve chargen answers as first-class structured fields on
    // the PC.  Threaded from the persisted answers map rather than
    // from the AI response so the player's verbatim wording survives
    // — the AI is asked to keep the backstory consistent with the
    // answers, not to re-render them.  When the player skipped a
    // question (older campaign without the id, or DM-direct
    // authoring), the field is omitted from the payload and the
    // materializer skips it too.
    //
    // Extended 2026-08-17 (chargen-artifact-loss sweep) to cover the
    // seven fields identified by the TTRPG review: alignment (was
    // silently unset — a first-class schema field with drift
    // mechanics keyed off it that the AI synth path never
    // populated), meaningfulItem + specificPlace (voice-sample /
    // grounder short-answers), plus temperament / prior-connection /
    // flight-reason / intent-horizon (MC values).
    type IntentSweepPayload = {
      intentionUnderPressure?: string;
      alignment?:
        | 'lawful-good'
        | 'neutral-good'
        | 'chaotic-good'
        | 'lawful-neutral'
        | 'true-neutral'
        | 'chaotic-neutral'
        | 'lawful-evil'
        | 'neutral-evil'
        | 'chaotic-evil';
      meaningfulItem?: string;
      specificPlace?: string;
      temperamentUnderPressure?: string;
      priorConnectionKind?: string;
      flightReason?: string;
      intentionHorizon?: string;
    };
    const intentPayload: IntentSweepPayload = {};
    {
      const campaignForIntent = this.env.getCurrentCampaign();
      if (campaignForIntent) {
        const slugForIntent = this.env.getCampaignSlug(campaignForIntent);
        const persistedForIntent = loadChargenState(slugForIntent, slot);
        const answersForIntent = persistedForIntent?.answers ?? {};
        // Short-answer helper: trim, reject whitespace-only.  The
        // materializer applies the same trim; the controller's copy
        // keeps the sheet renderer honest (any truthy string is
        // presumed rendered).
        const takeShortAnswer = (id: string): string | undefined => {
          const raw = answersForIntent[id];
          if (typeof raw !== 'string') return undefined;
          const trimmed = raw.trim();
          return trimmed.length > 0 ? trimmed : undefined;
        };
        // MC helper: accept the value verbatim (no length trim for
        // MC values — they're canonical enum strings, not player
        // prose).  Empty string treated as omission.
        const takeMcValue = (id: string): string | undefined => {
          const raw = answersForIntent[id];
          if (typeof raw !== 'string') return undefined;
          return raw.length > 0 ? raw : undefined;
        };
        const alignmentValue = takeMcValue('alignment');
        const VALID_ALIGNMENTS = [
          'lawful-good',
          'neutral-good',
          'chaotic-good',
          'lawful-neutral',
          'true-neutral',
          'chaotic-neutral',
          'lawful-evil',
          'neutral-evil',
          'chaotic-evil'
        ] as const;
        const intentMoment = takeShortAnswer('intent-moment');
        if (intentMoment !== undefined) {
          intentPayload.intentionUnderPressure = intentMoment;
        }
        // Only assign alignment when it matches the enum — a
        // corrupted campaign answer shouldn't torpedo the whole
        // pc-create (the materializer would reject it anyway; we
        // reject at the controller too so a well-formed but
        // wrong-value answer just skips the field, letting the DM
        // pc-edit it in later).
        if (
          alignmentValue !== undefined &&
          (VALID_ALIGNMENTS as readonly string[]).includes(alignmentValue)
        ) {
          intentPayload.alignment =
            alignmentValue as IntentSweepPayload['alignment'];
        }
        const meaningfulItem = takeShortAnswer('meaningful-item');
        if (meaningfulItem !== undefined) {
          intentPayload.meaningfulItem = meaningfulItem;
        }
        const specificPlace = takeShortAnswer('specific-place');
        if (specificPlace !== undefined) {
          intentPayload.specificPlace = specificPlace;
        }
        const temperament = takeMcValue('temperament-under-pressure');
        if (temperament !== undefined) {
          intentPayload.temperamentUnderPressure = temperament;
        }
        const priorConnection = takeMcValue('prior-connection');
        if (priorConnection !== undefined) {
          intentPayload.priorConnectionKind = priorConnection;
        }
        const flightReason = takeMcValue('flight-reason');
        if (flightReason !== undefined) {
          intentPayload.flightReason = flightReason;
        }
        const intentionHorizon = takeMcValue('intent-horizon');
        if (intentionHorizon !== undefined) {
          intentPayload.intentionHorizon = intentionHorizon;
        }
      }
    }

    const appended = this.env.appendPcCreate({
      pcId,
      name: r.name,
      pronouns: r.pronouns,
      tags: r.tags,
      stats: statsLower,
      skills: r.skillMastery,
      backstory: r.backstory,
      causedByResponseId: r.responseId,
      ...catchUp,
      ...phaseB,
      ...intentPayload
    });
    if (!appended) return; // host gated (non-coord, no session) — preserve invariant

    // Bind the slot now that the PC exists.
    this.env.bindPcSlot(slot, pcId);

    // D5.5-B (2026-05-27): emit the player's chargen bond drafts as
    // placeholder bond-propose events, AFTER pc-create + pc-slot-
    // bind so the D5-3 authoring gate sees the bound seat.  Each
    // draft is independent — the loop continues past a rejected
    // bond so one bad entry doesn't strand the rest; proposals are
    // DM-private so a partial emission is benign (vs. rolling back
    // the already-committed PC).  The DM resolves each placeholder
    // target to a real pcId at ratify time.
    const acceptCampaign = this.env.getCurrentCampaign();
    if (acceptCampaign) {
      const acceptSlug = this.env.getCampaignSlug(acceptCampaign);
      const persisted = loadChargenState(acceptSlug, slot);
      const drafts = persisted?.bondDrafts ?? [];
      if (drafts.length > 0) {
        let dropped = 0;
        for (const draft of drafts) {
          const ok = this.env.appendBondPropose({
            pcId,
            targetPlaceholder: draft.targetPlaceholder,
            text: draft.text
          });
          if (!ok) dropped++;
        }
        // Post-review fix #1: CONSUME the drafts after emission so a
        // later clearSynth → re-synth → re-accept doesn't re-emit
        // them as fresh proposals (the engine dedups by id, but
        // proposeBond mints a new random id each call, so re-emits
        // would NOT be caught — they'd pile up as duplicates).
        // Persist the rest of the chargen state unchanged.
        saveChargenState(acceptSlug, slot, {
          chosenPath: persisted?.chosenPath ?? '',
          answers: persisted?.answers ?? {},
          bondDrafts: []
        });
        // Post-review fix #3: surface dropped bonds (engine cap
        // reached / gate) in the audit trail rather than silently
        // losing them — every other acceptSlot side effect writes
        // a scratch-note, so this matches the audit posture.
        if (dropped > 0) {
          this.env.appendScratchNote(
            `${dropped} of ${drafts.length} chargen bond(s) for slot ${slot} ` +
              `were not accepted (bond cap reached or authoring gate).  ` +
              `Re-add via the bond queue if the PC should keep them.`
          );
        }
      }
    }

    // Audit-trail scratch-note retains the existing v1 shape — a
    // future audit tool can parse "DM accepted synthesized PC for
    // slot N: name=X, responseId=Y" without needing to know about
    // the pc-create event kind.
    this.acceptance.markAccepted(slot);
    this.env.appendScratchNote(
      `DM accepted synthesized PC for slot ${slot}: name="${r.name}", responseId=${r.responseId}.`
    );
    // Phase B P2 (2026-05-26) audit hook (verifier S3 refined):
    // when the synth response committed Phase-B fields, append a
    // separate audit-only scratch-note recording the committed
    // values.  This layer has NO answers lookup so it can't tell
    // sourced-from-player from AI-free invention — the note is
    // intentionally neutral ("present" not "inferred"); the diff-
    // lens provenance column in the modal is where the DM actually
    // sees field-level sourcing before clicking Accept.  Useful at
    // end-of-session diff review for "where did this language /
    // moneyBand come from?" forensics.
    const phaseBPresent: string[] = [];
    if (r.languages !== undefined && r.languages.length > 0) {
      phaseBPresent.push(`languages=${JSON.stringify(r.languages)}`);
    }
    if (r.moneyBand !== undefined) {
      phaseBPresent.push(`moneyBand=${r.moneyBand}`);
    }
    if (phaseBPresent.length > 0) {
      this.env.appendScratchNote(
        `Phase-B fields present at accept for slot ${slot}: ${phaseBPresent.join(', ')}.`
      );
    }
    // BUG-3 hotfix: drop persisted synth blob — the PC is now
    // materialized into shared state; the cached synth is no
    // longer needed and a stale blob would just clutter
    // localStorage.  Use the acceptSlug we already resolved above.
    if (acceptCampaign) {
      clearChargenSynthResult(
        this.env.getCampaignSlug(acceptCampaign),
        slot
      );
    }
    this.host.requestUpdate();
  }

  /**
   * Phase 3b polish (2026-05-23): DM hand-edits a spoiler-leak-
   * rejected synth result and accepts it.  Used when the spoiler
   * firewall caught the AI but most of the backstory is salvageable
   * — the DM rewrites the offending sentence(s) and commits.
   *
   * Replaces the slot's failed result with a synthesized ok result
   * whose backstory + name are the DM-edited values; other fields
   * (pronouns, tags, stats, skillMastery) come from the
   * rejectedResponse.  Then runs the normal acceptSlot flow.
   *
   * Returns false when the slot has no rejected response to edit
   * (the UI should hide the affordance in that case).
   */
  acceptWithEdits(
    slot: number,
    edits: { name: string; backstory: string }
  ): boolean {
    const failed = this.synth.getResult(slot);
    if (
      !failed ||
      failed.ok ||
      !failed.rejectedResponse
    ) {
      return false;
    }
    const cleaned: SynthesizeBackstoryResult = {
      ok: true,
      response: {
        ...failed.rejectedResponse,
        name: edits.name,
        backstory: edits.backstory
      },
      warnings: [
        {
          severity: 'warning',
          code: 'dm-hand-edited',
          message:
            'DM hand-edited the backstory after a spoiler-leak rejection.'
        }
      ],
      retried: true
    };
    this.synth.setResult(slot, cleaned);
    // BUG-3 hotfix: persist hand-edited result too — acceptSlot will
    // clear the persisted blob below, but if it bails for any reason
    // (no campaign, race) we still want the edit to survive reload.
    this.persistSynthResultIfPossible(slot, cleaned);
    this.host.requestUpdate();
    // Now commit via the normal accept path.
    this.acceptSlot(slot);
    return true;
  }

  /**
   * P3T-19 revise.  Drops the cached synth result + accept flag so
   * the region's seat shows the bare "ready to synthesize" state;
   * appends an audit scratch-note carrying the optional reason the
   * DM provides (the region's prompt asks for one).  Used when the
   * DM wants the player to revise an answer before re-synthesizing.
   */
  requestReviseSlot(
    slot: number,
    reason?: string,
    pinnedQuestionIds?: readonly string[]
  ): void {
    if (!this.synth.hasResult(slot) && !this.acceptance.isAccepted(slot)) return;
    // Post-R5 fix (QA-BUG-3): the re-sync about to land will
    // produce a new synth-result.  Revise would clear it anyway,
    // but firing both creates a race — refuse during the window.
    if (this.synth.isResyncInFlight(slot)) return;
    // Post-D5.5-A playthrough Scenario 1: revise on an already-
    // accepted slot is a silent data-leak — the engine has already
    // committed pc-create + pc-slot-bind, so wiping our local
    // acceptance state would leave a ghost PC in the player roster.
    // Refuse + audit; the DM must explicitly retire the bound PC
    // first (pc-retire, with narrative metadata).  The UI mirrors
    // this by disabling the Revise button on accepted slots so the
    // refusal path is normally unreachable; the controller-side
    // gate is the load-bearing defense.
    if (this.acceptance.isAccepted(slot)) {
      this.env.appendScratchNote(
        `DM asked to revise slot ${slot} but it is already-accepted.  ` +
          `Retire the bound PC first if you want to free the seat.`
      );
      this.host.requestUpdate();
      return;
    }
    this.synth.deleteResult(slot);
    // BUG-3 hotfix: clear persisted blob on revise so it doesn't
    // re-appear after a reload.
    {
      const reviseCampaign = this.env.getCurrentCampaign();
      if (reviseCampaign) {
        clearChargenSynthResult(
          this.env.getCampaignSlug(reviseCampaign),
          slot
        );
      }
    }
    this.acceptance.resetForRevise(slot);
    const trimmedReason = reason?.trim() ?? '';
    const parts: string[] = [
      trimmedReason
        ? `DM asked player at slot ${slot} to revise.  Reason: ${trimmedReason}`
        : `DM asked player at slot ${slot} to revise.`
    ];
    // Wave 3c: when the DM pinned some questions, record the list
    // in the scratch note so the audit trail (and eventually the
    // player-side chargen pre-fill) can reference it.
    if (pinnedQuestionIds && pinnedQuestionIds.length > 0) {
      parts.push(
        `Kept answers for: ${pinnedQuestionIds.join(', ')}.`
      );
    }
    this.env.appendScratchNote(parts.join('  '));
    this.host.requestUpdate();
  }

  // ---- chargen-route seeding ----

  /**
   * Seed the in-memory chargen state from localStorage for the
   * given campaign+slot.  Called by the chargen-route loader in
   * QuireApp when the player visits an `?invite=` URL.  When no
   * persisted state exists (first visit, or different device),
   * resets to empty.
   */
  seedFromStorage(slug: string, slot: number): void {
    const resumed = loadChargenState(slug, slot);
    if (resumed) {
      this.chosenPath = resumed.chosenPath;
      this.answers = resumed.answers;
      this.bondDrafts = resumed.bondDrafts;
    } else {
      this.chosenPath = '';
      this.answers = {};
      this.bondDrafts = [];
    }
    this.host.requestUpdate();
  }

  /**
   * D5.5-A: debounced persist of the current chargen state via
   * the extracted ChargenPersistenceQueue.  Per-slot (slug+slot)
   * key so concurrent edits to different slots don't stomp.
   */
  persistDebounced(campaign: ChargenCampaign, slot: number): void {
    const slug = this.env.getCampaignSlug(campaign);
    const key = `${slug}:${slot}`;
    this.persistQueue.schedule(key, {
      slug,
      slot,
      chosenPath: this.chosenPath,
      answers: { ...this.answers },
      bondDrafts: this.bondDrafts.map((d) => ({ ...d }))
    });
  }

  /**
   * Force any pending debounced persists to flush immediately.
   * Mirrors AiKeyStore.flushPending — used at known checkpoints
   * (chargen-route exit) and by tests so they don't need fake
   * timers.
   */
  flushPending(): void {
    this.persistQueue.flushAll();
  }

  /**
   * D5.5 first-session polish: queue-writer callback — fires on every
   * flush with whether the localStorage write actually landed.
   *   - success → "✓ Saved", fades back to idle after a short delay.
   *   - failure → "⚠ Couldn't save", which does NOT fade — the player
   *     must keep seeing it so they fall back to Pack/Send rather than
   *     trusting a write that silently failed (quota / sandbox).
   * A later successful flush clears the warning.
   */
  private reflectSave(ok: boolean): void {
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = null;
    }
    if (!ok) {
      this.saveState = 'save-failed';
      this.host.requestUpdate();
      return;
    }
    this.saveState = 'saved';
    this.host.requestUpdate();
    this.saveStateTimer = setTimeout(() => {
      this.saveStateTimer = null;
      if (this.saveState === 'saved') {
        this.saveState = 'idle';
        this.host.requestUpdate();
      }
    }, CHARGEN_SAVED_CLEAR_MS);
  }

  // ---- pack + download (CC-10) ----

  packAndDownload(campaign: ChargenCampaign, slot: number): void {
    try {
      const fingerprint = campaignFingerprint(campaign.base.source);
      const doc = packChargen({
        campaignFingerprint: fingerprint,
        slot,
        chosenPath: this.chosenPath,
        answers: this.answers,
        bondDrafts: this.bondDrafts
      });
      const json = stringifyChargenPack(doc);
      const filename = suggestedPackFilename(
        doc,
        this.env.getCampaignSlug(campaign)
      );
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      this.packFeedback = 'packed';
    } catch (e) {
      if (
        e instanceof ChargenPackError ||
        e instanceof Error // includes URL.createObjectURL failures in sandboxed envs
      ) {
        this.packFeedback = 'pack-failed';
      } else {
        throw e;
      }
    }
    this.host.requestUpdate();
    // Auto-clear feedback after 3 seconds so the next interaction
    // doesn't see stale text.
    if (this.packFeedbackTimer) clearTimeout(this.packFeedbackTimer);
    this.packFeedbackTimer = setTimeout(() => {
      this.packFeedbackTimer = null;
      if (
        this.packFeedback === 'packed' ||
        this.packFeedback === 'pack-failed'
      ) {
        this.packFeedback = '';
        this.host.requestUpdate();
      }
    }, PACK_FEEDBACK_CLEAR_MS);
  }

  /**
   * #254 (2026-05-26): request AI complementarity hints for the
   * quick-gen modal on `slot`.  Builds a roster snapshot from
   * already-bound PCs (DM-only view; safe — we're the coord),
   * calls the AI, and stores the result in
   * `complementarityHints[slot]` for the UI to render as chips.
   *
   * Async-side-effect: the UI subscribes via host.requestUpdate.
   * Returns immediately when no API key / already loading; caller
   * doesn't need to await.
   */
  async requestComplementarityHintsForSlot(
    slot: number,
    rosterFactory: () => import('../ai/complementarity-hints').RosterSnapshot,
    dmGuidance?: string
  ): Promise<void> {
    const apiKey = this.env.getAiApiKey();
    const model = this.env.getAiModel();
    if (apiKey.length === 0) {
      this.complementarityHints.set(slot, {
        status: 'failed',
        suggestions: [],
        reason: 'No API key configured — set one in the AI panel.'
      });
      this.host.requestUpdate();
      return;
    }
    const existing = this.complementarityHints.get(slot);
    if (existing?.status === 'loading') return;
    this.complementarityHints.set(slot, {
      status: 'loading',
      suggestions: []
    });
    this.host.requestUpdate();
    const providers = this.env.getAiProviders();
    const provider = providers[this.env.getAiProvider()];
    let roster: import('../ai/complementarity-hints').RosterSnapshot;
    try {
      roster = rosterFactory();
    } catch {
      roster = { pcs: [] };
    }
    const { requestComplementarityHints } = await import(
      '../ai/complementarity-hints'
    );
    const result = await requestComplementarityHints(provider, {
      apiKey,
      model,
      roster,
      dmGuidance
    });
    this.complementarityHints.set(
      slot,
      result.ok
        ? { status: 'ok', suggestions: result.suggestions }
        : {
            status: 'failed',
            suggestions: [],
            reason: result.reason ?? 'unknown failure'
          }
    );
    this.host.requestUpdate();
  }

  /**
   * #254: dismiss the hints panel for a slot (e.g., after the DM
   * picked a chip, OR clicked away).  The UI also calls this
   * when the quick-gen form is closed to clean up state.
   */
  dismissComplementarityHints(slot: number): void {
    if (this.complementarityHints.has(slot)) {
      this.complementarityHints.delete(slot);
      this.host.requestUpdate();
    }
  }

  /**
   * #253 (2026-05-26): live "Send to DM" — pack the player's
   * current chargen state and dispatch over the active WebRTC
   * session as a `chargen-pack-deliver` event.  Returns true on
   * append success; false when no session, no host wiring, or
   * the encoded pack exceeds the materializer's hard cap.
   *
   * Feedback state (`sendToDmFeedback`) auto-clears after
   * PACK_FEEDBACK_CLEAR_MS, same as `packFeedback` for the
   * download path.
   */
  packAndSendToDm(campaign: ChargenCampaign, slot: number): boolean {
    if (!this.env.appendChargenPackDeliver) {
      this.sendToDmFeedback = 'send-failed';
      this.scheduleSendToDmFeedbackClear();
      this.host.requestUpdate();
      return false;
    }
    let pack: ChargenPackDocument;
    try {
      const fingerprint = campaignFingerprint(campaign.base.source);
      pack = packChargen({
        campaignFingerprint: fingerprint,
        slot,
        chosenPath: this.chosenPath,
        answers: this.answers,
        bondDrafts: this.bondDrafts
      });
    } catch {
      this.sendToDmFeedback = 'send-failed';
      this.scheduleSendToDmFeedbackClear();
      this.host.requestUpdate();
      return false;
    }
    // Pre-send size check — surface "use file fallback" locally
    // BEFORE dispatching so the materializer doesn't silently drop
    // the event.  Same constant as the materializer cap (single
    // source of truth via chargen-pack).
    const encoded = JSON.stringify(pack);
    if (encoded.length > CHARGEN_PACK_MAX_SIZE_BYTES) {
      this.sendToDmFeedback = 'send-too-large';
      this.scheduleSendToDmFeedbackClear();
      this.host.requestUpdate();
      return false;
    }
    const ok = this.env.appendChargenPackDeliver(slot, pack);
    this.sendToDmFeedback = ok ? 'sent' : 'send-failed';
    this.scheduleSendToDmFeedbackClear();
    this.host.requestUpdate();
    return ok;
  }

  private scheduleSendToDmFeedbackClear(): void {
    if (this.sendToDmFeedbackTimer) {
      clearTimeout(this.sendToDmFeedbackTimer);
    }
    this.sendToDmFeedbackTimer = setTimeout(() => {
      this.sendToDmFeedbackTimer = null;
      this.sendToDmFeedback = '';
      this.host.requestUpdate();
    }, PACK_FEEDBACK_CLEAR_MS);
  }
  private sendToDmFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  // ---- DM-side pack import (Phase 3b polish 2026-05-22) ----

  /**
   * Accept a packed character JSON the DM received from a player
   * (file picker / drag-drop / future WebRTC delivery) and write
   * the answers into the slot's local persistence so the existing
   * Synthesize flow can consume them.  Returns a discriminated
   * result; the UI surfaces a precise message.
   *
   * Cross-campaign safety: the pack's campaignFingerprint must
   * match the currently-loaded campaign.  Slot mismatch is
   * surfaced as a typed error (the DM may have dropped the file
   * onto the wrong seat) so the UI can offer "Apply to slot N
   * instead?" or similar.
   */
  importPack(
    pack: ChargenPackDocument,
    targetSlot: number
  ):
    | { ok: true; appliedSlot: number }
    | {
        ok: false;
        code: 'no-campaign' | 'campaign-mismatch' | 'slot-mismatch';
        message: string;
      } {
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) {
      return {
        ok: false,
        code: 'no-campaign',
        message: 'No campaign loaded; cannot apply pack.'
      };
    }
    const fingerprint = campaignFingerprint(campaign.base.source);
    if (pack.campaignFingerprint !== fingerprint) {
      return {
        ok: false,
        code: 'campaign-mismatch',
        message:
          `This pack is for a different campaign (fingerprint mismatch).  ` +
          `Load the right campaign first, then re-import.`
      };
    }
    if (pack.slot !== targetSlot) {
      return {
        ok: false,
        code: 'slot-mismatch',
        message:
          `Pack was created for slot ${pack.slot}, but you dropped it on ` +
          `slot ${targetSlot}.  Apply to slot ${pack.slot}, or ask the ` +
          `player to re-pack with the correct invite link.`
      };
    }
    const slug = this.env.getCampaignSlug(campaign);
    saveChargenState(slug, pack.slot, {
      chosenPath: pack.chosenPath,
      answers: pack.answers,
      // D5.5-B: carry the player's bond drafts so acceptSlot can
      // emit them post-create.  Older 0.1.0 packs parse with
      // bondDrafts undefined → persisted as [].
      bondDrafts: pack.bondDrafts ?? []
    });
    // Wipe any cached synth result for this slot — the DM will
    // re-synth from the freshly-loaded answers.
    this.clearSynth(pack.slot);
    this.host.requestUpdate();
    return { ok: true, appliedSlot: pack.slot };
  }

  /**
   * Convenience helper: parse a raw JSON string and import it.
   * Lets the UI hand over the file contents directly without
   * needing to import the pack-parser separately.
   */
  importPackFromText(
    raw: string,
    targetSlot: number
  ):
    | { ok: true; appliedSlot: number }
    | {
        ok: false;
        code:
          | 'malformed'
          | 'no-campaign'
          | 'campaign-mismatch'
          | 'slot-mismatch';
        message: string;
      } {
    let parsed: ChargenPackDocument;
    try {
      parsed = parseChargenPack(raw);
    } catch (e) {
      if (e instanceof ChargenPackError) {
        return { ok: false, code: 'malformed', message: e.message };
      }
      return {
        ok: false,
        code: 'malformed',
        message: `Couldn't read pack file: ${(e as Error).message}`
      };
    }
    return this.importPack(parsed, targetSlot);
  }

  // ---- invite URL (CC-12) ----

  generateInviteUrl(slot: number): Promise<string | null> {
    if (!this.env.isCoordinator()) return Promise.resolve(null);
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) return Promise.resolve(null);
    if (!Number.isInteger(slot) || slot < 1 || slot > 9) {
      return Promise.resolve(null);
    }
    try {
      const fingerprint = campaignFingerprint(campaign.base.source);
      const token = encodeInviteToken({
        slot,
        issuedAt: Date.now(),
        campaignFingerprint: fingerprint
      });
      const search = routeToSearch({
        kind: 'character-creation',
        slug: this.env.getCampaignSlug(campaign),
        inviteToken: token
      });
      return Promise.resolve(
        `${window.location.origin}${window.location.pathname}${search}`
      );
    } catch {
      return Promise.resolve(null);
    }
  }

  // ---- code-split helpers ----

  loadChargenRegion(): Promise<void> {
    if (this.chargenRegionLoaded) return this.chargenRegionLoaded;
    this.chargenRegionLoaded = import(
      '../ui/regions/character-creation'
    ).then(() => undefined);
    return this.chargenRegionLoaded;
  }

  /**
   * Step 2: lazy-load the `<chargen-dm-review>` region.  Idempotent
   * — returns the in-flight promise so concurrent callers share one
   * fetch.  Subsequent re-mounts hit the cache.
   */
  loadDmReviewRegion(): Promise<void> {
    if (this.dmReviewRegionLoaded) return this.dmReviewRegionLoaded;
    this.dmReviewRegionLoaded = import(
      '../ui/regions/chargen-dm-review'
    ).then(() => {
      this.dmReviewRegionDefined = true;
      // BUG-3 hotfix (2026-05-30): rehydrate any persisted synth
      // results now that the region is defined.  This is the DM-
      // first-load path — if the user reloaded the tab between
      // Synthesize and Accept, the in-memory map is empty but
      // localStorage carries the result.  Idempotent on re-mount.
      this.hydrateSynthResultsFromStorage();
      this.host.requestUpdate();
    });
    return this.dmReviewRegionLoaded;
  }

  /** Set to true once `loadDmReviewRegion()` resolves. */
  dmReviewRegionDefined: boolean = false;
  private dmReviewRegionLoaded: Promise<void> | null = null;

  private loadSynthesizerModule(): Promise<
    typeof import('../ai/backstory-synthesizer')
  > {
    if (!this.synthesizerLoaded) {
      this.synthesizerLoaded = import('../ai/backstory-synthesizer');
    }
    return this.synthesizerLoaded;
  }

  // ---- end-to-end AI synthesis (CC-17 + CC-19 + CC-20 + CC-21 + CC-23) ----

  /**
   * End-to-end backstory synthesis for one slot.  Mirrors the
   * pre-extraction `quire-app.synthesizeBackstoryForSlot` verbatim,
   * but reads its dependencies via the host getters.  Returns the
   * frozen-contract `SynthesizeBackstoryResult` so the DM-review
   * region can consume the typed shape directly (no lossy adapter).
   */
  async synthesizeForSlot(
    slot: number,
    options: {
      playerDisplayName?: string;
      dmConstraints?: string;
      /**
       * DM-supplied answers used INSTEAD of the device-persisted
       * answers.  Set this to drive synthesis from the DM's quick-
       * generate form when no player answers exist on this device
       * — pass `{}` to synthesize purely from campaign context +
       * dmConstraints.  When omitted, the controller reads
       * persisted answers as before.
       */
      inlineAnswers?: CharCreationAnswers;
      /**
       * Wave 3b (2026-05-25): re-sync mode.  When set, the
       * synthesizer is told to honor the previous backstory's
       * voice + the locked-in DM-edited field values; output is
       * a new backstory that uses the edited values verbatim.
       */
      resync?: ResyncContext;
    } = {}
  ): Promise<SynthesizeBackstoryResult> {
    // Post-D5.5-A playthrough Scenario 4: capture the slot
    // generation BEFORE any await.  Pre-fix, the capture lived
    // after the campaign-context build await, which let a
    // synchronous clearSynth right after `synthesizeForSlot(slot)`
    // bump the counter unseen by this synthesis.  Capturing at
    // function entry (before any microtask boundary) is the
    // load-bearing position.
    const generationAtStart = this.synth.captureGeneration(slot);
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) {
      return {
        ok: false,
        code: 'provider-error',
        message: 'No campaign loaded; cannot synthesize.'
      };
    }
    if (!Number.isInteger(slot) || slot < 1 || slot > 9) {
      return {
        ok: false,
        code: 'provider-error',
        message: `Slot ${slot} is out of range [1, 9].`
      };
    }
    const slug = this.env.getCampaignSlug(campaign);
    // inlineAnswers wins when the DM is driving quick-generate.
    // Otherwise fall back to per-device persistence.
    let answersMap: CharCreationAnswers;
    if (options.inlineAnswers !== undefined) {
      answersMap = options.inlineAnswers;
    } else {
      const persisted = loadChargenState(slug, slot);
      if (!persisted) {
        return {
          ok: false,
          code: 'provider-error',
          message:
            `No chargen answers for slot ${slot} on this device.  ` +
            `Either load the player's packed character (the "Load packed character" button) ` +
            `or use Quick-generate to drive synthesis from a DM-supplied prompt.`
        };
      }
      answersMap = persisted.answers;
    }
    const provider = this.env.getAiProvider();
    const apiKey = this.env.getAiApiKey();
    if (!apiKey) {
      return {
        ok: false,
        code: 'provider-error',
        message: `No API key configured for provider "${provider}".`
      };
    }
    const declared =
      campaign.base.manifest.characterCreation?.questions ?? [];
    const answers: AnsweredQuestion[] = [];
    for (const q of declared) {
      const a = answersMap[q.id];
      if (a !== undefined && a !== '') {
        answers.push({ question: q, answer: a });
      }
    }
    let context: ContextFile[];
    try {
      context = await buildPlayerFacingContext({
        source: campaign.base.source,
        episodes: (campaign.base.manifest.episodes ?? []).map((slug) => ({
          slug
        })),
        characters: campaign.base.manifest.characters
      });
    } catch (e) {
      return {
        ok: false,
        code: 'provider-error',
        message: `Failed to build campaign context: ${(e as Error).message}`
      };
    }
    // P3D-1 hybrid seam: campaign-declared spoiler tokens + place
    // allowlist flow from `aiBackstory` in the manifest through to
    // the synthesizer.
    const aiBackstory = campaign.base.manifest.aiBackstory;
    const spoilerTokens = aiBackstory?.spoilerTokens;
    const placeAllowlist = aiBackstory?.placeAllowlist;
    this.synth.markSynthInFlight(slot);
    this.host.requestUpdate();
    try {
      const mod = await this.loadSynthesizerModule();
      const result = await mod.synthesizeBackstory(
        this.env.getAiProviders()[provider],
        {
          campaignContext: context,
          dmConstraints: options.dmConstraints ?? '',
          playerDisplayName: options.playerDisplayName ?? '',
          answers,
          apiKey,
          model: this.env.getAiModel(),
          spoilerTokens,
          validatorOptions: {
            playerDisplayName: options.playerDisplayName,
            placeAllowlist
          },
          ...(options.resync ? { resync: options.resync } : {})
        }
      );
      // Scenario-4 gate: caller (clearSynth) bumped the generation
      // while we were awaiting → the slot was wiped intentionally.
      // Return the result for the caller's inspection but DO NOT
      // touch synth result / acceptance state.  The caller in
      // resyncBackstoryForSlot also gates its setResyncFailure on
      // this same generation comparison.
      const generationNow = this.synth.currentGeneration(slot);
      if (generationNow !== generationAtStart) {
        return result;
      }
      this.synth.setResult(slot, result);
      // BUG-3 hotfix (2026-05-30): persist the synth result so a
      // tab reload between Synthesize and Accept rehydrates the
      // backstory + Accept button instead of dropping back to an
      // empty seat.  Best-effort: localStorage failures (quota,
      // sandbox) don't break the in-memory flow.
      this.persistSynthResultIfPossible(slot, result);
      // Re-synthesizing clears any prior accept for the same slot —
      // accepting the OLD result and then re-synthesizing would
      // otherwise leave the accept stale.  When this was a
      // successful re-sync, also clear drift Maps (AI folded them
      // into the new prose).
      this.acceptance.resetAfterFreshSynth(slot, {
        resultOk: result.ok,
        resyncSucceeded: !!options.resync && result.ok
      });
      return result;
    } finally {
      this.synth.clearSynthInFlight(slot);
      this.host.requestUpdate();
    }
  }

  /**
   * Wave 3b (2026-05-25): re-sync the backstory for a slot after
   * the DM has edited one or more synth-result fields.  Builds the
   * ResyncContext from the current synth result + drift snapshot
   * and delegates to synthesizeForSlot in resync mode.
   *
   * Returns null when the slot has no synth result OR no drift
   * to re-sync against — the UI should hide the verb in that case.
   */
  async resyncBackstoryForSlot(
    slot: number,
    options: { playerDisplayName?: string; dmConstraints?: string } = {}
  ): Promise<SynthesizeBackstoryResult | null> {
    if (this.synth.isResyncInFlight(slot)) return null;
    const synth = this.synth.getResult(slot);
    if (!synth || !synth.ok) return null;
    const drift = this.acceptance.getPreAcceptOriginal(slot);
    if (!drift || Object.keys(drift).length === 0) return null;
    const editedFields = Object.keys(drift) as Array<
      | 'name'
      | 'pronouns'
      | 'tags'
      | 'skillMastery'
      | 'stats'
      | 'languages'
      | 'moneyBand'
      | 'backstory'
    >;
    // Post-R5 fix (QA-BUG-5): voice-anchor uses the AI's ORIGINAL
    // backstory (pre-Patch) when one was stashed.  If the DM ran
    // Patch-in-place first (mutating the cached prose with
    // deterministic substitutions that may carry "they was"-style
    // verb-agreement glitches), the AI would otherwise faithfully
    // preserve those glitches as voice.  Falls back to the current
    // cached prose when no Patch has run.
    const previousBackstory =
      this.acceptance.getOriginalBackstoryForResync(slot) ??
      synth.response.backstory;
    // Phase B P2 (2026-05-26) verification fix (B1): include
    // languages + moneyBand in lockedFields so DM edits to either
    // field survive a re-sync.  Pre-fix, the AI silently dropped
    // them — see TTRPG-craft P2 review verdict.  Filter editedFields
    // to the prompt-supported subset (drop 'backstory' because the
    // prompt task line doesn't have a callout for it).
    const editedForPrompt = editedFields.filter(
      (f) => f !== 'backstory'
    ) as ResyncContext['editedFields'];
    const resync: ResyncContext = {
      lockedFields: {
        name: synth.response.name,
        pronouns: synth.response.pronouns,
        tags: synth.response.tags,
        skillMastery: synth.response.skillMastery,
        stats: synth.response.stats,
        ...(synth.response.languages !== undefined
          ? { languages: synth.response.languages }
          : {}),
        ...(synth.response.moneyBand !== undefined
          ? { moneyBand: synth.response.moneyBand }
          : {})
      },
      previousBackstory,
      editedFields: editedForPrompt
    };
    // Scenario-4 gate: capture the slot generation BEFORE the await
    // so a clearSynth mid-resync also suppresses the failure-banner
    // write below (synthesizeForSlot already suppresses its own
    // state-write under the same condition).
    const generationAtStart = this.synth.captureGeneration(slot);
    this.synth.markResyncInFlight(slot);
    // Clear any prior failure banner — we're trying again.
    this.acceptance.clearResyncFailure(slot);
    this.host.requestUpdate();
    try {
      const result = await this.synthesizeForSlot(slot, {
        ...options,
        resync
      });
      const generationNow = this.synth.currentGeneration(slot);
      if (generationNow !== generationAtStart) {
        // Slot cleared mid-resync; do NOT raise a failure banner
        // on a slot the DM intentionally wiped.
        return result;
      }
      // Post-R5 fix (QA-BUG-4): on failure, stash the code/message
      // so the UI surfaces a banner.  Drift remains intact (the
      // success path in synthesizeForSlot is the only one that
      // clears preAcceptOriginals when resync was set).
      if (result && !result.ok) {
        this.acceptance.setResyncFailure(slot, {
          code: result.code,
          message: result.message
        });
      }
      return result;
    } finally {
      this.synth.clearResyncInFlight(slot);
      this.host.requestUpdate();
    }
  }

  resyncFailuresMap(): ReadonlyMap<number, { code: string; message: string }> {
    return this.acceptance.resyncFailuresSnapshot();
  }

  dismissResyncFailure(slot: number): void {
    if (this.acceptance.clearResyncFailure(slot)) {
      this.host.requestUpdate();
    }
  }

  /**
   * Post-R5 fix (QA-BUG-3): the canonical "is re-sync in flight"
   * accessor.  UI + acceptSlot + requestReviseSlot + editSynth*
   * all gate on this.  Returns a Set snapshot so the UI's
   * @property comparison sees a fresh value (mirrors BUG-1 fix).
   */
  resyncInFlightSet(): ReadonlySet<number> {
    return this.synth.resyncInFlightSnapshot();
  }

  isResyncInFlight(slot: number): boolean {
    return this.synth.isResyncInFlight(slot);
  }

  /**
   * Phase B P2 verification fix (S2): snapshot accessor used by the
   * UI to render the race-mismatch banner.  Same fresh-snapshot
   * posture as `resyncInFlightSet` so Lit's @property comparison
   * detects the change.
   */
  acceptRaceMismatchSet(): ReadonlySet<number> {
    return this.acceptance.raceMismatchSnapshot();
  }

  /**
   * P-R12 (2026-05-25): "joining at session N" setter for a slot.
   * Default (no entry) → session 1 / no catch-up.  Clamped to
   * [1, 20] — outside that range is rejected silently.
   */
  setJoiningSessionForSlot(slot: number, n: number): void {
    if (!Number.isInteger(n) || n < 1 || n > 20) return;
    if (n === 1) {
      this.acceptance.clearJoiningSession(slot);
    } else {
      this.acceptance.setJoiningSession(slot, n);
    }
    this.host.requestUpdate();
  }

  joiningSessionForSlot(slot: number): number {
    return this.acceptance.getJoiningSession(slot);
  }

  joiningSessionMap(): ReadonlyMap<number, number> {
    return this.acceptance.joiningSessionSnapshot();
  }

  /** Forget a slot's synthesis state (DM rejected or wants to start over). */
  clearSynth(slot: number): void {
    // synth.clearSlot wipes the result + bumps the generation
    // counter (Scenario-4 gate); returns whether anything was
    // actually deleted.  OR with acceptance.isAccepted so the
    // host re-renders when a previously-accepted slot is wiped
    // (acceptance.resetForDelete clears the flag below).
    const had = this.synth.clearSlot(slot) || this.acceptance.isAccepted(slot);
    this.acceptance.resetForDelete(slot);
    // BUG-3 hotfix: drop the persisted synth blob so it doesn't
    // rehydrate on the next reload.  The synth.clearSlot above
    // already bumped the generation counter so any in-flight
    // synthesis won't write back.
    const campaign = this.env.getCurrentCampaign();
    if (campaign) {
      const slug = this.env.getCampaignSlug(campaign);
      clearChargenSynthResult(slug, slot);
    }
    if (had) this.host.requestUpdate();
  }

  /**
   * BUG-3 hotfix (2026-05-30): rehydrate cached synth results from
   * localStorage on app load.  Called by the host after the
   * campaign is loaded + the DM-review region is mounted, before
   * the DM can interact.  Idempotent — re-running is a no-op when
   * the in-memory map already has the slot.  Failures are silent
   * (corrupt/absent entries fall through to "no cached result").
   *
   * The user-visible effect: a DM who imported a pack, ran
   * Synthesize, then reloaded the tab before clicking Accept sees
   * the proposed backstory + Accept button rehydrate instead of
   * an empty slot.
   */
  hydrateSynthResultsFromStorage(): void {
    const campaign = this.env.getCurrentCampaign();
    if (!campaign) return;
    const slug = this.env.getCampaignSlug(campaign);
    const slots = this.env.getPcSlots();
    let anyRestored = false;
    for (const slotStr of Object.keys(slots)) {
      const slot = Number(slotStr);
      if (!Number.isInteger(slot) || slot < 1 || slot > 9) continue;
      if (this.synth.hasResult(slot)) continue;
      const persisted = loadChargenSynthResult(slug, slot);
      if (persisted) {
        this.synth.setResult(slot, persisted);
        anyRestored = true;
      }
    }
    if (anyRestored) this.host.requestUpdate();
  }

  // ---- display-name resolution (P3U-12) ----

  /**
   * Return the display name for the PC bound to a slot, or null if
   * the character file hasn't loaded yet.  The DM-review region
   * shows this in place of the raw pcId (UX P3U-12).  Triggers a
   * lazy load when the cache is cold so a subsequent re-render
   * shows the resolved name.
   */
  displayNameForBound(pcId: string): string | null {
    const loaded = this.env.getBoundCharacter(pcId);
    if (loaded) {
      // LoadedCharacter.record is the raw character JSON; `name` is
      // the canonical display field (every PC json has it).
      const name = loaded.record?.name;
      return typeof name === 'string' && name.length > 0 ? name : pcId;
    }
    this.env.loadCharacterByPcId(pcId);
    return null;
  }
}

/**
 * Phase 3b-1: derive a stable, unique pcId for a synthesized PC.
 * Shape: `slot-${N}-${first 8 chars of responseId-stripped-of-non-safe-chars}`.
 *
 * Properties:
 *   - Stable: same slot + same responseId → same pcId.  A retry of
 *     the same logical synthesis reproduces the id (rare, but the
 *     materializer's first-write-wins handles the duplicate.)
 *   - Unique-per-resynth: re-synthesis produces a new responseId
 *     (broker-side), so re-accept lands a new pcId.
 *   - Human-debuggable: the slot number is visible in the id, and
 *     the hash suffix is short enough to copy-paste.
 *   - PC_ID_RE-safe: stripped to `[A-Za-z0-9._-]` before composition.
 *
 * Returns null when the responseId is empty or sanitizes to empty
 * (defensive guard against future provider weirdness).
 */
/**
 * R6 QA-F1 fix: deep-clone the mutable nested objects on a synth
 * response so an external reference to (say) `result.response.stats`
 * doesn't observe a later `editSynthFieldPreAccept({ stats: ... })`
 * mutation.  The shallow `{...response}` spread used previously
 * left stats/tags/skillMastery sharing identity with the original
 * wrapper.  Other fields (name/pronouns/backstory/raw/responseId
 * /tokensIn/tokensOut) are primitives — no need to clone.
 */
function deepCloneSynthResponse(
  r: PcBackstorySynthesisResponse
): PcBackstorySynthesisResponse {
  return {
    ...r,
    stats: { ...r.stats },
    tags: [...r.tags],
    skillMastery: [...r.skillMastery]
  };
}

function derivePcId(slot: number, responseId: string): string | null {
  if (!Number.isInteger(slot) || slot < 1 || slot > 9) return null;
  const safe = responseId.replace(/[^A-Za-z0-9_-]/g, '');
  if (safe.length === 0) return null;
  const short = safe.slice(0, 8);
  return `slot-${slot}-${short}`;
}

/**
 * Wave 3a (2026-05-25): plain find-replace that escapes regex
 * metachars in the needle.  Used for name patching — the haystack
 * (backstory) may contain regex-special chars but the needle
 * (PC name) is treated literally.
 */
function replaceAll(haystack: string, needle: string, replacement: string): string {
  if (needle.length === 0) return haystack;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return haystack.replace(new RegExp(escaped, 'g'), replacement);
}

/**
 * Wave 3a: deterministic pronoun substitution.  Handles the SAFE
 * subset: subject (she/he/they/ze), reflexive (herself/himself/
 * themselves/zirself), and possessive-determiner-when-followed-by-
 * a-noun-stem (hers/his/theirs/zirs).
 *
 * Deliberately DOES NOT touch the ambiguous "her"/"him" forms —
 * "her" can be object ("saw her") or possessive ("her keys"); the
 * correct substitute differs ("them" vs "their").  Same for "his"
 * vs "him".  Wave 3b adds an AI verb-fixup pass that disambiguates.
 *
 * Limitations (documented for Wave 3b followup):
 *   - Doesn't fix verb agreement ("she was" → "they were").
 *   - Doesn't handle capitalized sentence-start variants robustly
 *     (matches case-sensitively only).
 *   - Does NOT touch unknown pronoun sets (custom pronouns); falls
 *     through to a single safe rewrite of subject only.
 */
function applyPronounSubstitutions(
  text: string,
  fromPronouns: string,
  toPronouns: string
): string {
  const from = parsePronounSet(fromPronouns);
  const to = parsePronounSet(toPronouns);
  if (!from || !to) {
    // Unknown set — punt; user can revise manually.
    return text;
  }
  let out = text;
  // Order matters: longer substitutions first so we don't partially
  // match a shorter form embedded in a longer one.
  const subs: Array<[string, string]> = [
    [from.reflexive, to.reflexive],
    [from.possessive, to.possessive],
    [from.subject, to.subject]
  ];
  // Word-boundary regex to avoid matching inside other words
  // ("she" → "they" should not touch "shed" or "sherbet").
  for (const [a, b] of subs) {
    if (a === b) continue;
    const escaped = a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`\\b${escaped}\\b`, 'g'), b);
  }
  return out;
}

/**
 * Wave 3a: lookup table for the SAFE pronoun substitution subset.
 * Subject + reflexive + possessive-determiner.  Skips object form
 * ("her"/"him") and possessive-pronoun ("hers" vs "his") because
 * they disambiguate context-dependently.
 *
 * Returns null for unknown pronoun strings; caller falls back to
 * a no-op (AI re-sync in Wave 3b will handle custom sets).
 */
function parsePronounSet(
  raw: string
): { subject: string; reflexive: string; possessive: string } | null {
  const t = raw.trim().toLowerCase();
  if (t === 'she/her' || t === 'she') {
    return { subject: 'she', reflexive: 'herself', possessive: 'hers' };
  }
  if (t === 'he/him' || t === 'he') {
    return { subject: 'he', reflexive: 'himself', possessive: 'his' };
  }
  if (t === 'they/them' || t === 'they') {
    return { subject: 'they', reflexive: 'themselves', possessive: 'theirs' };
  }
  if (t === 'ze/zir' || t === 'ze') {
    return { subject: 'ze', reflexive: 'zirself', possessive: 'zirs' };
  }
  return null;
}
